import {apiHost, getProjectID} from 'common/report/util';
import {isBrowserEnvironment} from 'common/report/util/env';
// TODO tech-debt - resolve this circular import
/* eslint-disable-next-line import/no-cycle */
import {Store, createSimpleClient} from 'common/report/stores/store';

interface EndpointInfo {
  /** when set, if the current project id is included in the list, the request will be sent to python3 */
  projects?: number[];

  /** when set, a percentage of project ids will be sent to python3 */
  percentage?: number;
}

export interface Endpoints {
  /** object with keys representing regular expressions */
  python3_endpoints: {[key: string]: EndpointInfo};
}

// window state singleton
const ENDPOINTS_DEFAULTS = {
  initializePromise: null,
  initialized: false,
  store: null,
};

export function getEndpoints() {
  return isBrowserEnvironment()
    ? (window[`_py3EndpointsState`] = window[`_py3EndpointsState`] || ENDPOINTS_DEFAULTS)
    : ENDPOINTS_DEFAULTS;
}

/**
 * Appends python3 headers to a network request if it's
 * defined in /api/app/endpoints and matches the given URL
 */
export async function addPy3AppHeaders(url: string) {
  const endpointsURL = `${apiHost()}/api/app/py-migration/`;
  const endpoints = getEndpoints();
  if (url === endpointsURL || !getProjectID()) {
    return {};
  }

  // read from cache once per page load
  if (!endpoints.initializePromise) {
    endpoints.store = createPyMigrationStore();
    endpoints.initializePromise = new Promise((resolve) =>
      endpoints.store.subscribe(() => {
        endpoints.initialized = true;
        resolve();
      }),
    );
    endpoints.store.fetch();
  }

  // await to fetch cache data on first request
  if (!endpoints.initialized) {
    await endpoints.initializePromise;
  }

  const projectID = Number(getProjectID());
  const match = getEndpointMatch(url, projectID);
  return match ? {'x-webapp-python': `python3`} : {};
}

/** matches a given URL to loaded patterns from /api/app/endpoints */
function getEndpointMatch(url: string, projectID: number) {
  url = url.split(`?`)[0];

  for (const [pattern, {projects, percentage}] of getEndpoints().store.value) {
    const matches = pattern.test(url) && (projects ? projects.includes(projectID) : projectID % 100 < percentage);

    if (matches) {
      return {pattern, projects, percentage};
    }
  }

  return null;
}

/**
 * Scope `Store` creation in a function as opposed to module evaluation since
 * this store is used in `common/util/api-client.ts` which is imported in
 * `iron/common/report/stores/store.ts` and creates a circular dependency
 *  resulting in evaluating `Store` when it is not defined
 */
function createPyMigrationStore() {
  return new Store<Map<RegExp, EndpointInfo>>({
    apiClient: createSimpleClient(`api/app/py-migration/`),

    transform: {
      index: (response) => {
        const endpoints: Endpoints = response.results;
        const patterns = new Map<RegExp, EndpointInfo>();

        Object.entries(endpoints.python3_endpoints).forEach(([key, endpoint]) => {
          patterns.set(new RegExp(key), endpoint);
        });

        return patterns;
      },
    },

    defaultValue: new Map<RegExp, EndpointInfo>(),
  });
}
